import React, { useState, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { flashMessageStore } from '@/store/flashMessage';
import { FlashMessage } from '../Presentation';
import { FLASH_MESSAGE_LIFETIME_MILLISEC } from '@/constants';

export const FlashMessageContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isWarning, body } = useAppSelector(
    flashMessageStore.selectors.flashMessageSelector,
  );

  const [visible, setVisible] = useState(false);
  const timersRef = useRef<{ fadeOut?: NodeJS.Timeout; clear?: NodeJS.Timeout }>({});
  
  const show = () => setVisible(true);
  const erase = () => setVisible(false);
  
  const handleClose = () => {
    // Clear existing timers
    if (timersRef.current.fadeOut) clearTimeout(timersRef.current.fadeOut);
    if (timersRef.current.clear) clearTimeout(timersRef.current.clear);
    
    // Start fade out immediately
    erase();
    
    // Clear store after animation
    const clearTimer = setTimeout(() => {
      dispatch(flashMessageStore.actions.clear());
    }, 800);
    
    timersRef.current = { clear: clearTimer };
  };

  useEffect(() => {
    if (!body) {
      return;
    }

    show();
    
    // Start fade out after FLASH_MESSAGE_LIFETIME_MILLISEC
    const fadeOutTimer = setTimeout(() => {
      erase();
    }, FLASH_MESSAGE_LIFETIME_MILLISEC);
    
    // Clear store after fade out animation completes (800ms)
    const clearTimer = setTimeout(() => {
      dispatch(flashMessageStore.actions.clear());
    }, FLASH_MESSAGE_LIFETIME_MILLISEC + 800);
    
    timersRef.current = { fadeOut: fadeOutTimer, clear: clearTimer };
    
    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(clearTimer);
    };
  }, [body, dispatch]);

  return <FlashMessage body={body} isWarning={isWarning} visible={visible} onClose={handleClose} />;
};
